import { useTranslation } from "react-i18next"

import type { MyUsage } from "@/api/fair-use"

import { Lock } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"

import { PlanSummary } from "./plan-summary"
import { useFairUseStore } from "../fair-use-store"
import {
  clampPercentForDisplay,
  formatMonthName,
  formatResetDate,
  formatSek,
  getUsageColorBand,
  roundPercent,
  shouldShowRequestCta,
  usageProgressIndicatorClass,
} from "../fair-use-utils"

type UsagePopoverProps = {
  usage: MyUsage
}

/**
 * Popover content for the composer usage indicator (spec section 7): headline
 * with percent + month, horizontal progress bar filled to percent, spend and
 * reset date, plus status notices and — from the request-CTA threshold — a
 * "Request more" button that hands over to the request-more dialog.
 */
export function UsagePopover({ usage }: UsagePopoverProps) {
  const { t, i18n } = useTranslation()
  const openRequestDialog = useFairUseStore((state) => state.openRequestDialog)

  const locale = i18n.resolvedLanguage ?? i18n.language
  const percent = roundPercent(usage.percent)
  const band = getUsageColorBand(usage.percent, usage.status)
  const month = formatMonthName(usage.year_month, locale)
  const resetDate = formatResetDate(usage.reset_at, locale)

  return (
    <div className="flex flex-col gap-3" data-testid="fair-use-popover">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-bold">{t("fairUse.popover.title")}</h4>
        {usage.status === "downgrade" && (
          <Badge variant="warning">{t("fairUse.popover.downgradeBadge")}</Badge>
        )}
        {usage.status === "block" && (
          <Badge variant="destructive">
            <Lock aria-hidden="true" className="mr-1 size-3" />
            {t("fairUse.popover.blockedBadge")}
          </Badge>
        )}
      </div>
      {/* Fair use round 2: the plan line is no longer hidden for Standard.
          Standard is the plan that LOCKS things, so its holder is precisely
          the user who needs to see which plan they are on and what it does
          not include. */}
      <PlanSummary usage={usage} />
      <p className="text-sm">{t("fairUse.popover.headline", { percent, month })}</p>
      <div className="flex flex-col gap-1.5">
        <Progress
          aria-label={t("fairUse.indicator.ariaLabel", { percent })}
          className="h-2"
          indicatorClassName={usageProgressIndicatorClass(band)}
          value={clampPercentForDisplay(usage.percent)}
        />
        <div className="text-muted-foreground flex items-center justify-between text-xs">
          {/* No spend line while the amount is unknown (`cost_sek === null`):
              a live WS merge moved the percent without carrying a cost, so the
              last fetched amount would contradict the headline right above it
              ("You have used 68 %" over "0.00 of 2.00 SEK"). The percent, the
              bar and the reset date are all still true, so only this figure
              goes; the next frame or snapshot brings it straight back. */}
          {usage.cost_sek != null && (
            <span data-testid="fair-use-popover-spend">
              {t("fairUse.popover.spend", {
                cost: formatSek(usage.cost_sek),
                cap: formatSek(usage.cap_sek),
              })}
            </span>
          )}
          {/* `ml-auto` keeps the reset date anchored right whether or not the
              spend line is rendered, so it does not jump across the row when a
              metadata frame drops the amount. */}
          {resetDate && (
            <span className="ml-auto">{t("fairUse.popover.resetsOn", { date: resetDate })}</span>
          )}
        </div>
      </div>
      {usage.status === "downgrade" && (
        <p className="text-muted-foreground text-xs">{t("fairUse.popover.downgradeNotice")}</p>
      )}
      {usage.status === "block" && (
        <p className="text-muted-foreground text-xs">{t("fairUse.popover.blockedNotice")}</p>
      )}
      {usage.request?.pending && (
        <p className="text-muted-foreground text-xs" data-testid="fair-use-pending-note">
          {t("fairUse.popover.requestPending")}
        </p>
      )}
      {shouldShowRequestCta(usage) && (
        <Button
          className="w-full"
          data-testid="fair-use-request-more"
          size="sm"
          onClick={() => openRequestDialog()}
        >
          {t("fairUse.popover.requestMore")}
        </Button>
      )}
    </div>
  )
}
